import { Button, Card, CardContent, Divider, Grid, TextField, Typography } from '@mui/material'
import { NeedsClusterReinstalledDetailsDto, NeedsClusterReinstalledDtoSchema, VehicleCheckInDetailsDto } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import VehicleClusterReinstallStepper from './VehicleClusterReinstallStepper'
import { ArrowRight } from '@mui/icons-material'
import { GreenText } from 'components'

interface Step1OdometerReadingDetailsProps {
  initialValues: NeedsClusterReinstalledDetailsDto
  checkInValues: VehicleCheckInDetailsDto
  onNext: (values: NeedsClusterReinstalledDetailsDto) => void
  vehicleGuid?: string
}

function convertKmToMiles(km: number): number {
  const MILES_PER_KM = 0.6213711922
  return Math.round(km * MILES_PER_KM)
}

function convertMilesToKm(mi: number): number {
  const KM_PER_MILE = 1.60934
  return Math.round(mi * KM_PER_MILE)
}

interface UpdateDifferenceProps {
  difference: number | null
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

function UpdateDifference({ difference, setFieldValue }: UpdateDifferenceProps) {
  useEffect(() => {
    if (difference !== null) {
      setFieldValue('clusterReinstallOdometerReadingMiDifference', difference, false)
    } else {
      setFieldValue('clusterReinstallOdometerReadingMiDifference', undefined, false)
    }
  }, [difference, setFieldValue])

  return null
}

export default function Step1OdometerReadingDetails({
  initialValues,
  checkInValues,
  onNext
}: Step1OdometerReadingDetailsProps) {
  const [isLoading] = useState<boolean>(false)

  const [hasSubmitted, setHasSubmitted] = useState(false)

  let vehicleCheckInOdometerReadingKm = 0
  let vehicleCheckInOdometerReadingMi = 0

  if (checkInValues.vehicleCheckInOdometerReadingUnitOfMeasurement === 'Miles') {
    vehicleCheckInOdometerReadingMi = checkInValues.vehicleCheckInOdometerReadingMileage ?? 0

    vehicleCheckInOdometerReadingKm = convertMilesToKm(vehicleCheckInOdometerReadingMi)
  } else {
    vehicleCheckInOdometerReadingKm = checkInValues.vehicleCheckInOdometerReadingMileage ?? 0
    vehicleCheckInOdometerReadingMi = convertKmToMiles(vehicleCheckInOdometerReadingKm)
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validate={values => {
      //   const errors: Record<string, string> = {}
      //   const currentMiles = values.clusterReinstallOdometerReadingMi ?? 0
      //   const difference = currentMiles - vehicleCheckInOdometerReadingMi

      //   if (difference < 0 || difference > 100) {
      //     errors.clusterReinstallOdometerReadingMi = 'A mileage discrepancy was detected. Please check with management.'
      //   }

      //   return errors
      // }}
      onSubmit={(values, formikHelpers) => {
        setHasSubmitted(true)
        onNext(values)
        formikHelpers.setSubmitting(false)
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={NeedsClusterReinstalledDtoSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        setTouched,
        values,
        validateForm
      }) => {
        let difference: number | null = null
        if (values.clusterReinstallOdometerReadingMi !== null) {
          difference = Number(values.clusterReinstallOdometerReadingMi) - vehicleCheckInOdometerReadingMi
        }

        const isDiscrepancy = difference !== null && (difference < 0 || difference >= 100)

        return (
          <form onSubmit={handleSubmit}>
            <UpdateDifference difference={difference} setFieldValue={setFieldValue} />
            <Card>
              <CardContent>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <VehicleClusterReinstallStepper activeStep={0} />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} marginTop={1}>
                    <Typography fontWeight='bold' variant='body1'>
                      Vehicle Check-In Odometer Reading:
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography color='primary' fontWeight='inherit' variant='body1'>
                      {vehicleCheckInOdometerReadingKm.toLocaleString()} Kilometers
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      Vehicle Check-In Odometer Reading - Converted To Miles
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography color='primary' fontWeight='inherit' variant='body1'>
                      {vehicleCheckInOdometerReadingMi.toLocaleString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} container marginTop={2}>
                    <Grid item xs={9}>
                      <TextField
                        disabled={isLoading}
                        // error={Boolean(touched.clusterReinstallOdometerReadingMi && errors.clusterReinstallOdometerReadingMi)}
                        // helperText={touched.clusterReinstallOdometerReadingMi && errors.clusterReinstallOdometerReadingMi}
                        label='Current Odometer Reading'
                        type='number'
                        name='clusterReinstallOdometerReadingMi'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.clusterReinstallOdometerReadingMi || ''}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <Typography fontWeight='bold' variant='body1'>
                        Miles
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} marginTop={2}>
                    <Typography fontWeight='bold' variant='body1'>
                      Difference:
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography color={hasSubmitted && isDiscrepancy ? 'error' : 'primary '} fontWeight='bold' variant='body1'>
                      {values.clusterReinstallOdometerReadingMiDifference?.toLocaleString()} Miles
                    </Typography>
                  </Grid>

                  {hasSubmitted && isDiscrepancy && (
                    <>
                      <Grid item xs={12}>
                        <Typography color='error' fontWeight='inherit' variant='body1'>
                          Mileage Discrepancy Detected.
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography color='error' fontWeight='inherit' variant='body1'>
                          Please check with management about what to do with this vehicle.
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} marginTop={1}>
                    <Button
                      color='primary'
                      disabled={!Boolean(values.clusterReinstallOdometerReadingMi) || isSubmitting}
                      endIcon={<ArrowRight />}
                      fullWidth
                      type='submit'
                      variant='contained'
                    >
                      NEXT
                    </Button>
                  </Grid>

                  <Grid item xs={12} marginTop={1}>
                    <GreenText variant='outlined'>
                      {`**Vehicle Needs Cluster Reinstalled Steps:**

Displays the current step of the vehicle in the Needs Cluster
Reinstall process.

**Vehicle Check-In Odometer Reading:**

Prepopulated with the Exporter Mileage from the Vehicle Details –
Exporter tab.

**Vehicle Check-In Odometer Reading Converted To Miles:**

Prepopulated with the Exporter Mileage from the Vehicle Details – 
Exporter tab, converted to miles.

**Current Odometer Reading:**

A required free-text field for numeric input only.

**Difference:**

Displays the difference between the Vehicle Check-In Odometer
Reading – Converted To Miles value and the Current Odometer
Reading.

**NEXT Button:**

Clicking the NEXT button will:

Advance the user to the next step in the Needs Cluster Reinstalled
process.

Run a validation check to ensure the mileage is within acceptable
limits:

If the Current Odometer Reading equals Vehicle Check-In Odometer
Reading Converted to Miles + 100 Miles or more:

   - Display a warning: "Mileage Discrepancy Detected. Please check
with management about what to do with this vehicle."

   - Send a "Mileage Discrepancy Notification" email.

   - Return the user to the Needs Cluster Reinstalled to-do list screen.

If the Current Odometer Reading is less than Vehicle Check-In
Odometer Reading Converted to Miles:

   - Display a warning: "Mileage Discrepancy Detected. Please check
with management about what to do with this vehicle."

   - Send a "Mileage Discrepancy Notification" email.

If no Mileage Discrepancy is detected:

   - Advance the user to the Cluster Reinstalled Photo Capture
(gallery) screen.

`}
                    </GreenText>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </form>
        )
      }}
    </Formik>
  )
}
